import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdContentCopy } from 'react-icons/md';
import '../master/MasterData.css';

interface BOMItem {
  materialCode: string;
  materialName: string;
  quantity: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  scrapPercentage: number;
}

interface BOM {
  bomId: number;
  productCode: string;
  productName: string;
  version: string;
  effectiveDate: string;
  expiryDate: string;
  baseQuantity: number;
  unit: string;
  totalMaterialCost: number;
  laborCost: number;
  overheadCost: number;
  totalCost: number;
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'OBSOLETE';
  items: BOMItem[];
  createdBy: string;
  remarks: string;
}

const BillOfMaterials: React.FC = () => {
  const [boms, setBoms] = useState<BOM[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBOM, setEditingBOM] = useState<BOM | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    productCode: '',
    productName: '',
    version: '',
    effectiveDate: '',
    expiryDate: '',
    baseQuantity: 1,
    unit: 'pcs',
    laborCost: 0,
    overheadCost: 0,
    remarks: ''
  });
  const [items, setItems] = useState<BOMItem[]>([]);
  const [newItem, setNewItem] = useState({
    materialCode: '',
    materialName: '',
    quantity: 0,
    unit: 'pcs',
    unitCost: 0,
    scrapPercentage: 0
  });

  useEffect(() => {
    // TODO: Fetch BOMs from API
    setBoms([
      {
        bomId: 1,
        productCode: 'FG-001',
        productName: 'Executive Office Chair - Black',
        version: 'V1.0',
        effectiveDate: '2025-01-01',
        expiryDate: '',
        baseQuantity: 1,
        unit: 'pcs',
        totalMaterialCost: 2100,
        laborCost: 600,
        overheadCost: 300,
        totalCost: 3000,
        status: 'ACTIVE',
        items: [
          {
            materialCode: 'RM-001',
            materialName: 'Polyurethane Foam - High Density',
            quantity: 3.5,
            unit: 'kg',
            unitCost: 150,
            totalCost: 525,
            scrapPercentage: 5
          },
          {
            materialCode: 'RM-002',
            materialName: 'Fabric - Premium Grade',
            quantity: 2.0,
            unit: 'meters',
            unitCost: 200,
            totalCost: 400,
            scrapPercentage: 10
          },
          {
            materialCode: 'RM-005',
            materialName: 'Metal Frame - Chair Base',
            quantity: 1,
            unit: 'pcs',
            unitCost: 800,
            totalCost: 800,
            scrapPercentage: 0
          },
          {
            materialCode: 'RM-010',
            materialName: 'Hydraulic Cylinder',
            quantity: 1,
            unit: 'pcs',
            unitCost: 250,
            totalCost: 250,
            scrapPercentage: 0
          },
          {
            materialCode: 'RM-015',
            materialName: 'Caster Wheels (Set of 5)',
            quantity: 1,
            unit: 'set',
            unitCost: 125,
            totalCost: 125,
            scrapPercentage: 0
          }
        ],
        createdBy: 'Production Manager',
        remarks: 'Standard production BOM'
      },
      {
        bomId: 2,
        productCode: 'FG-002',
        productName: '3-Seater Sofa - Grey',
        version: 'V2.1',
        effectiveDate: '2025-02-01',
        expiryDate: '',
        baseQuantity: 1,
        unit: 'pcs',
        totalMaterialCost: 11500,
        laborCost: 2500,
        overheadCost: 1000,
        totalCost: 15000,
        status: 'ACTIVE',
        items: [
          {
            materialCode: 'RM-001',
            materialName: 'Polyurethane Foam - High Density',
            quantity: 25,
            unit: 'kg',
            unitCost: 150,
            totalCost: 3750,
            scrapPercentage: 5
          },
          {
            materialCode: 'RM-002',
            materialName: 'Fabric - Premium Grade',
            quantity: 12,
            unit: 'meters',
            unitCost: 200,
            totalCost: 2400,
            scrapPercentage: 10
          },
          {
            materialCode: 'RM-020',
            materialName: 'Wooden Frame - Sofa',
            quantity: 1,
            unit: 'set',
            unitCost: 4500,
            totalCost: 4500,
            scrapPercentage: 2
          },
          {
            materialCode: 'RM-025',
            materialName: 'Springs and Padding Kit',
            quantity: 1,
            unit: 'set',
            unitCost: 850,
            totalCost: 850,
            scrapPercentage: 0
          }
        ],
        createdBy: 'Production Manager',
        remarks: 'Updated design - Version 2.1'
      },
      {
        bomId: 3,
        productCode: 'FG-001',
        productName: 'Executive Office Chair - Black',
        version: 'V0.9',
        effectiveDate: '2024-01-01',
        expiryDate: '2024-12-31',
        baseQuantity: 1,
        unit: 'pcs',
        totalMaterialCost: 2000,
        laborCost: 550,
        overheadCost: 300,
        totalCost: 2850,
        status: 'OBSOLETE',
        items: [],
        createdBy: 'Production Manager',
        remarks: 'Old version - replaced by V1.0'
      }
    ]);
  }, []);

  const filteredBOMs = boms.filter(bom =>
    bom.productCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bom.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bom.version.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingBOM(null);
    setViewMode(false);
    setFormData({
      productCode: '',
      productName: '',
      version: 'V1.0',
      effectiveDate: new Date().toISOString().split('T')[0],
      expiryDate: '',
      baseQuantity: 1,
      unit: 'pcs',
      laborCost: 0,
      overheadCost: 0,
      remarks: ''
    });
    setItems([]);
    setNewItem({
      materialCode: '',
      materialName: '',
      quantity: 0,
      unit: 'pcs',
      unitCost: 0,
      scrapPercentage: 0
    });
    setShowModal(true);
  };

  const handleEdit = (bom: BOM) => {
    setEditingBOM(bom);
    setViewMode(false);
    setFormData({
      productCode: bom.productCode,
      productName: bom.productName,
      version: bom.version,
      effectiveDate: bom.effectiveDate,
      expiryDate: bom.expiryDate,
      baseQuantity: bom.baseQuantity,
      unit: bom.unit,
      laborCost: bom.laborCost,
      overheadCost: bom.overheadCost,
      remarks: bom.remarks
    });
    setItems([...bom.items]);
    setShowModal(true);
  };

  const handleView = (bom: BOM) => {
    setEditingBOM(bom);
    setViewMode(true);
    setFormData({
      productCode: bom.productCode,
      productName: bom.productName,
      version: bom.version,
      effectiveDate: bom.effectiveDate,
      expiryDate: bom.expiryDate,
      baseQuantity: bom.baseQuantity,
      unit: bom.unit,
      laborCost: bom.laborCost,
      overheadCost: bom.overheadCost,
      remarks: bom.remarks
    });
    setItems([...bom.items]);
    setShowModal(true);
  };

  const handleCopy = (bomId: number) => {
    const bomToCopy = boms.find(b => b.bomId === bomId);
    if (!bomToCopy) return;

    setEditingBOM(null);
    setViewMode(false);

    // Find next version number
    const productBOMs = boms.filter(b => b.productCode === bomToCopy.productCode);
    const versions = productBOMs.map(b => parseFloat(b.version.replace('V', '')));
    const nextVersion = Math.max(...versions) + 0.1;

    setFormData({
      productCode: bomToCopy.productCode,
      productName: bomToCopy.productName,
      version: `V${nextVersion.toFixed(1)}`,
      effectiveDate: new Date().toISOString().split('T')[0],
      expiryDate: '',
      baseQuantity: bomToCopy.baseQuantity,
      unit: bomToCopy.unit,
      laborCost: bomToCopy.laborCost,
      overheadCost: bomToCopy.overheadCost,
      remarks: `Copied from ${bomToCopy.version}`
    });
    setItems([...bomToCopy.items]);
    setShowModal(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddItem = () => {
    if (!newItem.materialCode || !newItem.materialName || newItem.quantity <= 0 || newItem.unitCost <= 0) {
      alert('Please fill in all required item fields');
      return;
    }

    const totalCost = newItem.quantity * newItem.unitCost;

    const item: BOMItem = {
      ...newItem,
      totalCost
    };

    setItems([...items, item]);
    setNewItem({
      materialCode: '',
      materialName: '',
      quantity: 0,
      unit: 'pcs',
      unitCost: 0,
      scrapPercentage: 0
    });
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!formData.productCode || !formData.productName || !formData.version || !formData.effectiveDate || items.length === 0) {
      alert('Please fill in all required fields and add at least one material item');
      return;
    }

    const totalMaterialCost = items.reduce((sum, item) => sum + item.totalCost, 0);
    const totalCost = totalMaterialCost + formData.laborCost + formData.overheadCost;

    const newBOM: BOM = {
      bomId: editingBOM ? editingBOM.bomId : boms.length + 1,
      productCode: formData.productCode,
      productName: formData.productName,
      version: formData.version,
      effectiveDate: formData.effectiveDate,
      expiryDate: formData.expiryDate,
      baseQuantity: formData.baseQuantity,
      unit: formData.unit,
      totalMaterialCost,
      laborCost: formData.laborCost,
      overheadCost: formData.overheadCost,
      totalCost,
      status: 'DRAFT',
      items: [...items],
      createdBy: 'Current User', // TODO: Get from auth context
      remarks: formData.remarks
    };

    if (editingBOM) {
      setBoms(boms.map(b => b.bomId === editingBOM.bomId ? newBOM : b));
      alert('BOM updated successfully!');
    } else {
      setBoms([newBOM, ...boms]);
      alert('BOM created successfully!');
    }

    setShowModal(false);
  };

  const handleDelete = (bomId: number) => {
    if (window.confirm('Are you sure you want to delete this BOM?')) {
      setBoms(boms.filter(b => b.bomId !== bomId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'badge-success';
      case 'DRAFT': return 'badge-warning';
      case 'INACTIVE': return 'badge-secondary';
      case 'OBSOLETE': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Bill of Materials (BOM)</h2>
            <p className="subtitle">Define material composition for products</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New BOM
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search BOMs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product Code</th>
                  <th>Product Name</th>
                  <th>Version</th>
                  <th>Effective Date</th>
                  <th>Base Qty</th>
                  <th>Material Cost</th>
                  <th>Labor Cost</th>
                  <th>Total Cost</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBOMs.map(bom => (
                  <tr key={bom.bomId}>
                    <td className="code">{bom.productCode}</td>
                    <td className="name">{bom.productName}</td>
                    <td><code>{bom.version}</code></td>
                    <td>{new Date(bom.effectiveDate).toLocaleDateString('en-US')}</td>
                    <td style={{ textAlign: 'center' }}>{bom.baseQuantity} {bom.unit}</td>
                    <td className="amount">₱{bom.totalMaterialCost.toLocaleString()}</td>
                    <td className="amount">₱{bom.laborCost.toLocaleString()}</td>
                    <td className="amount" style={{ fontWeight: 'bold' }}>₱{bom.totalCost.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(bom.status)}`}>
                        {bom.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(bom)}
                          title="View Details"
                        >
                          <MdVisibility />
                        </button>
                        {bom.status !== 'OBSOLETE' && (
                          <>
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(bom)}
                              title="Edit"
                            >
                              <MdEdit />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={() => handleCopy(bom.bomId)}
                              title="Copy BOM"
                              style={{ color: '#17a2b8' }}
                            >
                              <MdContentCopy />
                            </button>
                          </>
                        )}
                        {bom.status === 'DRAFT' && (
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(bom.bomId)}
                            title="Delete"
                          >
                            <MdDelete />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredBOMs.length === 0 && (
              <div className="no-data">
                <p>No BOMs found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1100px' }}>
            <div className="modal-header">
              <h3>{viewMode ? 'View BOM Details' : editingBOM ? 'Edit BOM' : 'Create New BOM'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {/* Product Information */}
              <div className="form-row">
                <div className="form-group">
                  <label>Product Code <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.productCode}
                    onChange={(e) => handleFormChange('productCode', e.target.value)}
                    placeholder="Enter product code"
                    disabled={viewMode}
                  />
                </div>
                <div className="form-group">
                  <label>Product Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.productName}
                    onChange={(e) => handleFormChange('productName', e.target.value)}
                    placeholder="Enter product name"
                    disabled={viewMode}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Version <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => handleFormChange('version', e.target.value)}
                    placeholder="e.g., V1.0"
                    disabled={viewMode}
                  />
                </div>
                <div className="form-group">
                  <label>Base Quantity <span style={{ color: 'red' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="number"
                      value={formData.baseQuantity}
                      onChange={(e) => handleFormChange('baseQuantity', Number(e.target.value))}
                      min="1"
                      disabled={viewMode}
                      style={{ flex: '2' }}
                    />
                    <select
                      value={formData.unit}
                      onChange={(e) => handleFormChange('unit', e.target.value)}
                      disabled={viewMode}
                      style={{ flex: '1' }}
                    >
                      <option value="pcs">Pieces</option>
                      <option value="kg">Kilograms</option>
                      <option value="set">Set</option>
                      <option value="box">Box</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Effective Date <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => handleFormChange('effectiveDate', e.target.value)}
                    disabled={viewMode}
                  />
                </div>
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => handleFormChange('expiryDate', e.target.value)}
                    disabled={viewMode}
                  />
                </div>
              </div>

              <hr style={{ margin: '20px 0' }} />

              {/* Material Items Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '15px' }}>Material Components</h4>

                {!viewMode && (
                  <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', marginBottom: '15px' }}>
                    <h5 style={{ marginBottom: '10px', fontSize: '14px' }}>Add Material</h5>
                    <div className="form-row">
                      <div className="form-group" style={{ flex: '1' }}>
                        <label style={{ fontSize: '12px' }}>Material Code <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="text"
                          value={newItem.materialCode}
                          onChange={(e) => setNewItem({ ...newItem, materialCode: e.target.value })}
                          placeholder="Material code"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group" style={{ flex: '2' }}>
                        <label style={{ fontSize: '12px' }}>Material Name <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="text"
                          value={newItem.materialName}
                          onChange={(e) => setNewItem({ ...newItem, materialName: e.target.value })}
                          placeholder="Material name"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Quantity <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="number"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem({ ...newItem, quantity: Number(e.target.value) })}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Unit <span style={{ color: 'red' }}>*</span></label>
                        <select
                          value={newItem.unit}
                          onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                          style={{ fontSize: '13px' }}
                        >
                          <option value="pcs">Pieces</option>
                          <option value="kg">Kilograms</option>
                          <option value="meters">Meters</option>
                          <option value="liters">Liters</option>
                          <option value="set">Set</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Unit Cost <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="number"
                          value={newItem.unitCost}
                          onChange={(e) => setNewItem({ ...newItem, unitCost: Number(e.target.value) })}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Scrap %</label>
                        <input
                          type="number"
                          value={newItem.scrapPercentage}
                          onChange={(e) => setNewItem({ ...newItem, scrapPercentage: Number(e.target.value) })}
                          placeholder="0"
                          min="0"
                          max="100"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddItem}
                          style={{ fontSize: '13px', padding: '8px 15px' }}
                        >
                          <MdAdd /> Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Materials Table */}
                {items.length > 0 ? (
                  <div className="data-table-container" style={{ marginTop: '15px' }}>
                    <table className="data-table" style={{ fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th>Material Code</th>
                          <th>Material Name</th>
                          <th>Quantity</th>
                          <th>Unit</th>
                          <th>Unit Cost</th>
                          <th>Total Cost</th>
                          <th>Scrap %</th>
                          {!viewMode && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td className="code">{item.materialCode}</td>
                            <td>{item.materialName}</td>
                            <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                            <td>{item.unit}</td>
                            <td className="amount">₱{item.unitCost.toLocaleString()}</td>
                            <td className="amount" style={{ fontWeight: 'bold' }}>₱{item.totalCost.toLocaleString()}</td>
                            <td style={{ textAlign: 'center' }}>{item.scrapPercentage}%</td>
                            {!viewMode && (
                              <td>
                                <button
                                  className="btn-icon btn-delete"
                                  onClick={() => handleRemoveItem(index)}
                                  title="Remove"
                                >
                                  <MdDelete />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                          <td colSpan={5} style={{ textAlign: 'right' }}>TOTAL MATERIAL COST:</td>
                          <td className="amount" style={{ fontWeight: 'bold', fontSize: '14px' }}>
                            ₱{items.reduce((sum, item) => sum + item.totalCost, 0).toLocaleString()}
                          </td>
                          <td colSpan={viewMode ? 1 : 2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="no-data" style={{ padding: '20px', fontSize: '13px' }}>
                    <p>No materials added yet</p>
                  </div>
                )}
              </div>

              <hr style={{ margin: '20px 0' }} />

              {/* Cost Summary */}
              <div className="form-row">
                <div className="form-group">
                  <label>Labor Cost</label>
                  <input
                    type="number"
                    value={formData.laborCost}
                    onChange={(e) => handleFormChange('laborCost', Number(e.target.value))}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    disabled={viewMode}
                  />
                </div>
                <div className="form-group">
                  <label>Overhead Cost</label>
                  <input
                    type="number"
                    value={formData.overheadCost}
                    onChange={(e) => handleFormChange('overheadCost', Number(e.target.value))}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    disabled={viewMode}
                  />
                </div>
              </div>

              {/* Total Cost Display */}
              <div style={{ backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '4px', marginTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>Total Material Cost</p>
                    <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#1976d2' }}>
                      ₱{items.reduce((sum, item) => sum + item.totalCost, 0).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>Labor + Overhead</p>
                    <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#1976d2' }}>
                      ₱{(formData.laborCost + formData.overheadCost).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>TOTAL COST</p>
                    <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#0d47a1' }}>
                      ₱{(items.reduce((sum, item) => sum + item.totalCost, 0) + formData.laborCost + formData.overheadCost).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => handleFormChange('remarks', e.target.value)}
                  placeholder="Enter remarks"
                  rows={3}
                  disabled={viewMode}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                {viewMode ? 'Close' : 'Cancel'}
              </button>
              {!viewMode && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!formData.productCode || !formData.productName || !formData.version || !formData.effectiveDate || items.length === 0}
                >
                  {editingBOM ? 'Update BOM' : 'Save BOM'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default BillOfMaterials;
